import type { RouterOutputs } from "@apps/server/trpc/routers";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  LoadingOverlay,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendar, IconCar, IconCheck, IconLoader } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Draggable,
  Eventcalendar,
  type MbscCalendarEvent,
  type MbscCalendarEventData,
  type MbscEventCreatedEvent,
  type MbscEventcalendarView,
  type MbscEventUpdateEvent,
  type MbscResource,
} from "mobiscroll";
import { useCallback, useMemo, useState } from "react";
import { useBoolean } from "~/hooks/use-boolean";
import { logger } from "~/lib/logger";
import { trpc } from "~/lib/trpc";
import { ManageCrewsDrawer } from "./-components/manage-crews-drawer";

type Deal = RouterOutputs["scheduler"]["getDealsByDate"][number];

// Extended event type with custom properties
type ExtendedCalendarEvent = MbscCalendarEvent & {
  dealId?: number;
  scheduleId?: number;
  customerName?: string;
};

export const Route = createFileRoute("/_authenticated/scheduler/$date")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc }, params: { date } }) => {
    const [deals, crews, dealTableConfig] = await Promise.all([
      queryClient.ensureQueryData(trpc.scheduler.getDealsByDate.queryOptions({ date })),
      queryClient.ensureQueryData(trpc.scheduler.getCrewsByDate.queryOptions({ date })),
      queryClient.ensureQueryData(trpc.crm.getFieldConfigsByTable.queryOptions({ table: "crm.deal" })),
    ]);
    return { deals, crews, date, dealTableConfig };
  },
});

function RouteComponent() {
  const loaderData = Route.useLoaderData();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const crewDrawer = useBoolean();

  const handleDateChange = (date: string | null) => {
    if (!date) return;
    navigate({ to: "/scheduler/$date", params: { date } });
  };

  const crews = useQuery({
    ...trpc.scheduler.getCrewsByDate.queryOptions({ date: loaderData.date }),
    initialData: loaderData.crews,
  });

  const deals = useQuery({
    ...trpc.scheduler.getDealsByDate.queryOptions({ date: loaderData.date }),
    initialData: loaderData.deals,
  });

  const { scheduledDeals, nonScheduledDeals } = useMemo(() => {
    const scheduled: Deal[] = [];
    const nonScheduled: Deal[] = [];
    (deals.data || loaderData.deals).forEach((deal) => {
      if (deal.fieldSchedule) {
        scheduled.push(deal);
      } else {
        nonScheduled.push(deal);
      }
    });
    return { scheduledDeals: scheduled, nonScheduledDeals: nonScheduled };
  }, [deals.data, loaderData.deals]);

  const addScheduleMutation = useMutation(
    trpc.scheduler.addScheduleForDeal.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.scheduler.getDealsByDate.queryKey() });
      },
    }),
  );

  const updateScheduleMutation = useMutation(
    trpc.scheduler.updateScheduleForDeal.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.scheduler.getDealsByDate.queryKey() });
      },
    }),
  );

  // Helper function to format Date to HH:MM:SS
  const formatTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  // Helper function to extract crew ID from resource ID
  const extractCrewId = (resourceId: string | number | (string | number)[] | undefined): number | null => {
    if (!resourceId) return null;
    // If it's an array, take the first element
    const resource = Array.isArray(resourceId) ? resourceId[0] : resourceId;
    if (!resource) return null;
    const resourceStr = String(resource);
    // Resource IDs are in format "crewId-userId" for members, or just "crewId" for crew groups
    const parts = resourceStr.split("-");
    const crewId = Number.parseInt(parts[0], 10);
    return Number.isNaN(crewId) ? null : crewId;
  };

  const view = useMemo<MbscEventcalendarView>(() => {
    return {
      timeline: {
        type: "day",
        startTime: "06:00:00",
        endTime: "22:00:00",
        maxEventStack: "all",
      },
    };
  }, []);

  const resources = useMemo<MbscResource[]>(() => {
    return (crews.data || loaderData.crews).map(
      (crew) =>
        ({
          id: crew.id,
          name: crew.name,
          children: crew.members.map((member) => ({
            id: `${crew.id}-${member.userId}`,
            name: member.user.name,
            eventCreation: false,
            description: "Staff",
          })),
        }) satisfies MbscResource,
    );
  }, [loaderData.crews, crews.data]);

  const events = useMemo<ExtendedCalendarEvent[]>(() => {
    const _events: ExtendedCalendarEvent[] = [];

    scheduledDeals.forEach((deal) => {
      if (!deal.fieldSchedule || !deal.serviceDate) return;
      const startTime = deal.fieldSchedule.start;
      const endTime = deal.fieldSchedule.end;
      const serviceDate = deal.serviceDate;
      const crewId = deal.fieldSchedule.fieldCrewId;
      const customerName = deal.customer?.name || "Unknown Customer";

      _events.push({
        id: deal.id,
        start: `${serviceDate}T${startTime}`,
        end: `${serviceDate}T${endTime}`,
        title: `${customerName} - ${deal.service}`,
        resource: crewId, // Assign to the crew resource
        color: "#4dabf5",
        dealId: deal.id,
        scheduleId: deal.fieldSchedule.id, // Store scheduleId for updates
        customerName: customerName,
      });
    });

    return _events;
  }, [scheduledDeals]);

  const renderResource = useCallback((resource: MbscResource) => {
    if (resource.children) {
      return <Center h="100%">{resource.name}</Center>;
    }

    return (
      <Group wrap="nowrap">
        {resource.description === "Car" ? (
          <ThemeIcon variant="light" size="lg" color="blue">
            <IconCar size={20} />
          </ThemeIcon>
        ) : (
          <Avatar color="initials" name={resource.name} />
        )}
        <div>
          <Text size="sm" fw={500}>
            {resource.name}
          </Text>
        </div>
      </Group>
    );
  }, []);

  const renderScheduleEvent = useCallback((data: MbscCalendarEventData) => {
    const original = data.original as ExtendedCalendarEvent;

    const startTime = formatDisplayTime(original.start);
    const endTime = formatDisplayTime(original.end);

    return (
      <Paper
        shadow="xs"
        radius="sm"
        p={4}
        style={{
          height: "100%",
          backgroundColor: original.color || "var(--mantine-color-dark-6)",
          color: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "2px",
        }}>
        {original.customerName && (
          <Box
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              padding: "2px 6px",
              borderRadius: "4px",
              marginBottom: "2px",
            }}>
            <Text size="xs" fw={700} truncate style={{ letterSpacing: "0.3px" }}>
              {original.customerName}
            </Text>
          </Box>
        )}
        <Text size="xs" fw={original.customerName ? 500 : 700} truncate>
          {data.title}
        </Text>
        <Text size="xs" truncate style={{ opacity: 0.8, fontSize: "10px" }}>
          {startTime} - {endTime}
        </Text>
      </Paper>
    );
  }, []);

  const handleEventUpdate = useCallback(
    (event: MbscEventUpdateEvent) => {
      logger.debug("Event update", event);
      const updatedEvent = event.event as ExtendedCalendarEvent;

      if (!updatedEvent.scheduleId || !updatedEvent.start || !updatedEvent.end) {
        logger.error("Missing required data for event update");
        return;
      }

      const start = updatedEvent.start instanceof Date ? updatedEvent.start : new Date(updatedEvent.start as string);
      const end = updatedEvent.end instanceof Date ? updatedEvent.end : new Date(updatedEvent.end as string);

      // Extract crew ID from the resource
      const crewId = extractCrewId(updatedEvent.resource);
      if (!crewId) {
        logger.error("Invalid crew ID");
        return;
      }

      updateScheduleMutation.mutate({
        scheduleId: updatedEvent.scheduleId,
        crewId: crewId,
        start: formatTime(start),
        end: formatTime(end),
      });
    },
    [updateScheduleMutation, extractCrewId, formatTime],
  );

  const handleEventCreated = useCallback(
    (args: MbscEventCreatedEvent) => {
      const { event, action } = args;

      // Check if this is an external drop event (dragging from sidebar)
      if (action === "externalDrop" && event.dealId && event.start && event.end && event.resource) {
        // Handle different types for start and end
        const startValue =
          typeof event.start === "string" || event.start instanceof Date ? event.start : new Date().toISOString();
        const endValue =
          typeof event.end === "string" || event.end instanceof Date ? event.end : new Date().toISOString();

        const start = startValue instanceof Date ? startValue : new Date(startValue);
        const end = endValue instanceof Date ? endValue : new Date(endValue);

        // Extract crew ID from the resource
        const crewId = extractCrewId(event.resource);
        if (!crewId) {
          logger.error("Invalid crew ID");
          return;
        }

        addScheduleMutation.mutate({
          dealId: event.dealId,
          crewId: crewId,
          start: formatTime(start),
          end: formatTime(end),
        });
      }
    },
    [addScheduleMutation, extractCrewId, formatTime],
  );

  return (
    <Box style={{ height: "calc(100vh - 100px)", display: "flex", flexDirection: "row", gap: "1rem" }}>
      {/* Sidebar with non-scheduled deals */}
      {nonScheduledDeals.length > 0 && (
        <Paper
          shadow="sm"
          h="100dvh"
          w="300px"
          p="sm"
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--mantine-color-body)",
          }}>
          <Group justify="space-between" mb="md">
            <Title order={4}>Unscheduled Deals</Title>
            <Badge size="lg" color="orange.4">
              {nonScheduledDeals.length}
            </Badge>
          </Group>
          <ScrollArea>
            <Stack gap="sm">
              {nonScheduledDeals.map((deal) => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </Stack>
          </ScrollArea>
        </Paper>
      )}

      {/* Main calendar area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Group justify="space-between" p="xs">
          <DateInput
            value={new Date(loaderData.date)}
            onChange={handleDateChange}
            leftSection={<IconCalendar size={16} />}
            placeholder="Select date"
            valueFormat="YYYY-MM-DD"
            style={{ width: 200 }}
            highlightToday
            popoverProps={{ zIndex: 1000 }}
          />
          <Group>
            {/* Save Status Indicator */}
            <Group gap="xs" style={{ minWidth: "165px" }}>
              {addScheduleMutation.isPending || updateScheduleMutation.isPending ? (
                <>
                  <IconLoader size={18} style={{ animation: "spin 1s linear infinite" }} />
                  <Text size="sm" c="dimmed">
                    Saving...
                  </Text>
                </>
              ) : (
                <>
                  <IconCheck size={18} color="green" />
                  <Text size="sm" c="dimmed">
                    All changes saved
                  </Text>
                </>
              )}
            </Group>
            <Button variant="light" onClick={crewDrawer.open}>
              Manage Crews
            </Button>
            <Badge size="lg" variant="light" color="teal">
              {scheduledDeals.length} Scheduled
            </Badge>
          </Group>
        </Group>
        <Box pos="relative" flex={1}>
          <LoadingOverlay
            visible={!crewDrawer.state && (crews.data == null || crews.data.length === 0)}
            loaderProps={{
              children: (
                <Card>
                  <Alert mb="lg" color="orange">
                    You must create one or more crews to schedule deals
                  </Alert>
                  <Center>
                    <Button variant="light" onClick={crewDrawer.open}>
                      Manage Crews
                    </Button>
                  </Center>
                </Card>
              ),
            }}
          />
          <Eventcalendar
            view={view}
            data={events}
            resources={resources}
            selectedDate={loaderData.date}
            dragToCreate={false}
            dragToMove={true}
            dragToResize={true}
            clickToCreate={false}
            showControls={false}
            externalDrop={true}
            externalDrag={true}
            onEventCreated={handleEventCreated}
            renderResource={renderResource}
            renderScheduleEvent={renderScheduleEvent}
            onEventUpdate={handleEventUpdate}
            cssClass="md-custom-event"
            themeVariant="dark"
            eventOverlap={false}
          />
        </Box>
      </div>
      <ManageCrewsDrawer date={loaderData.date} opened={crewDrawer.state} onClose={crewDrawer.close} />
    </Box>
  );
}

// Format time to HH:mm
const formatDisplayTime = (dateTime: string | Date | object | undefined): string => {
  if (!dateTime) return "";
  const date = typeof dateTime === "string" ? new Date(dateTime) : dateTime instanceof Date ? dateTime : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// DealCard component for draggable non-scheduled deals
const DealCard = ({ deal }: { deal: Deal }) => {
  const [draggable, setDraggable] = useState<HTMLDivElement>();

  const setDragElm = useCallback((elm: HTMLDivElement | null) => {
    if (elm) setDraggable(elm);
  }, []);

  // Create the event data for Mobiscroll
  const eventData: ExtendedCalendarEvent = {
    id: deal.id,
    title: `${deal.customer?.name} - ${deal.service}`,
    color: "#ff9800",
    dealId: deal.id,
    // Set a default duration of 2 hours
    start: new Date().toISOString(),
    end: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
  };

  return (
    <Paper
      ref={setDragElm}
      shadow="sm"
      p="sm"
      c="gray.1"
      bg="dark.5"
      style={{
        cursor: "grab",
        borderLeft: "4px solid var(--mantine-color-orange-4)",
      }}>
      <Group justify="space-between" wrap="nowrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {deal.customer?.name} - {deal.service}
          </Text>
          {deal.customer?.name && (
            <Text size="xs" c="dimmed" truncate>
              {deal.customer.name}
            </Text>
          )}
        </div>
      </Group>
      {draggable && <Draggable dragData={eventData} element={draggable} />}
    </Paper>
  );
};
